import { z } from 'zod';
import {
  ARTIFACT_MAX_LEVEL,
  addEffects,
  effectsAtLevel,
  findArtifact,
  NO_ARTIFACT_EFFECTS,
  type ScaledArtifactEffects,
} from './artifacts';
/*
 * O que este módulo NÃO importa mais: nada de `./jewels` (gate V28F.A).
 *
 * Havia aqui uma ramificação que agrupava as joias soberanas POSSUÍDAS e somava
 * 100 bps ao total. Sob o contrato do V28D.2 isso é exactamente errado: um
 * Coração Primordial parado no inventário passaria a conceder dano, e o efeito
 * vem da INCORPORAÇÃO numa nave, que é uma propriedade da nave e não da conta.
 *
 * A ausência do import é a prova estrutural: este resolvedor não tem como ler
 * uma joia, e por isso não tem como conceder por posse.
 */

/**
 * Which artifacts an account's passives actually come from.
 *
 * Two rules, and everything here exists to enforce them:
 *
 * 1. **Different artifacts stack.** Owning Guns and Rich gives both.
 * 2. **Copies of the SAME artifact never stack.** Owning three Guns gives one
 *    Guns - the highest-level copy, and only that one.
 *
 * The consequence that matters for correctness: passives are DERIVED from the
 * inventory every time, never accumulated into a column. Nothing can be
 * granted twice by replaying a request, and selling the strongest copy simply
 * promotes the next one on the very next resolution.
 *
 * A reserved or listed artifact still belongs to the player, so it keeps its
 * passive until a transfer actually completes. Reserving is a promise to
 * trade, not a transfer.
 */

/** One owned artifact instance, as the resolver receives it. */
export interface OwnedArtifact {
  readonly instanceId: string;
  readonly definitionId: string;
  readonly level: number;
  /** `owned` is the only state that counts; consumed and retired do not. */
  readonly state: string;
}

/** What one artifact contributes, and why. */
export interface ArtifactPassive {
  readonly definitionId: string;
  /** The copy whose level is being applied. Deterministic on a tie. */
  readonly instanceId: string;
  readonly level: number;
  readonly effects: ScaledArtifactEffects;
  /** Every owned copy of this artifact, strongest first. */
  readonly copies: readonly OwnedArtifact[];
}

/**
 * What the account's passives are, and there is exactly one source of them.
 *
 * ## O que saiu daqui no gate V28F.A
 *
 * `sovereignJewels`, e o tipo `SovereignJewelPassive` com ele. O Coração
 * Primordial era passivo por POSSE e somava 100 bps a este total; passou a ser
 * incorporado numa nave, e a contagem incorporada é uma propriedade da NAVE
 * EQUIPADA - não da conta, e não deste resolvedor.
 *
 * Nada foi movido para outro campo aqui: neste gate a contagem não altera
 * combate nenhum. O que existe é a persistência (`ship_instances`), a operação
 * atómica e os contratos de leitura.
 */
export interface ResolvedPassives {
  /** One entry per DEFINITION owned, never per copy. */
  readonly artifacts: readonly ArtifactPassive[];
  /** The sum across definitions. */
  readonly total: ScaledArtifactEffects;
}

export const EMPTY_PASSIVES: ResolvedPassives = Object.freeze({
  artifacts: [],
  total: NO_ARTIFACT_EFFECTS,
});

/**
 * Highest level wins; the instance id breaks a tie.
 *
 * The tie-break is for DISPLAY only - which copy is labelled active. The
 * effect is applied once either way, because only one entry per definition
 * ever reaches the total.
 */
function strongestFirst(a: OwnedArtifact, b: OwnedArtifact): number {
  if (a.level !== b.level) {
    return b.level - a.level;
  }
  return a.instanceId < b.instanceId ? -1 : a.instanceId > b.instanceId ? 1 : 0;
}

/**
 * Derives the account's passives from what it owns right now.
 *
 * Pure and total: unknown definitions, non-artifact items, consumed copies and
 * impossible levels are skipped rather than guessed at, so a row that should
 * not exist cannot become a bonus.
 */
export function resolvePassives(owned: readonly OwnedArtifact[]): ResolvedPassives {
  const byDefinition = new Map<string, OwnedArtifact[]>();
  for (const instance of owned) {
    if (instance.state !== 'owned') {
      continue;
    }
    if (!findArtifact(instance.definitionId)) {
      continue;
    }
    if (
      !Number.isSafeInteger(instance.level) ||
      instance.level < 1 ||
      instance.level > ARTIFACT_MAX_LEVEL
    ) {
      continue;
    }
    const bucket = byDefinition.get(instance.definitionId);
    if (bucket) {
      bucket.push(instance);
    } else {
      byDefinition.set(instance.definitionId, [instance]);
    }
  }

  const artifacts: ArtifactPassive[] = [];
  let total = NO_ARTIFACT_EFFECTS;
  // Sorted by definition id so the summary is byte-stable: it goes into the
  // session snapshot, and a reordering would read as a state change.
  for (const definitionId of [...byDefinition.keys()].sort()) {
    const copies = (byDefinition.get(definitionId) ?? []).slice().sort(strongestFirst);
    const best = copies[0];
    const definition = findArtifact(definitionId);
    if (!best || !definition) {
      continue;
    }
    const effects = effectsAtLevel(definition, best.level);
    artifacts.push({
      definitionId,
      instanceId: best.instanceId,
      level: best.level,
      effects,
      copies,
    });
    // Once per DEFINITION: this is where "copies do not stack" is enforced.
    total = addEffects(total, effects);
  }

  return { artifacts, total };
}

/* -------------------------------------------------------------------------- */
/*  Persistence                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The frozen summary a hunt session carries.
 *
 * Small on purpose: the instance ids, the levels and the totals - enough to
 * re-simulate a window exactly, to fingerprint the epoch, and to prove which
 * copies were live when a reward was credited. Bigints cross as decimal
 * strings, like every other unbounded value in this project.
 */
export const artifactPassiveSummarySchema = z.object({
  /** Bumped when the artifact catalog's meaning changes. */
  contentVersion: z.number().int().min(1),
  entries: z.array(
    z.object({
      definitionId: z.string().min(1),
      instanceId: z.uuid(),
      level: z.number().int().min(1).max(ARTIFACT_MAX_LEVEL),
    }),
  ),
  /**
   * As joias soberanas que estavam vivas quando a sessão congelou (V28B).
   *
   * **Sempre vazia desde o gate V28F.A.** O campo FICA, e `.default([])` fica
   * com ele, exatamente pela razão pela qual entrou: sessões escritas antes
   * disto existir carregam-no, e uma sessão em curso não pode deixar de ser
   * legível por causa de um campo que mudou de significado. Uma linha antiga com
   * uma joia lá dentro continua a parsear, e o total que ela congelou continua a
   * ser o total com que aquela corrida foi jogada - uma sessão congela os seus
   * passivos, e o V28F.A não retroage sobre corridas antigas.
   *
   * O que já não acontece é uma sessão NOVA escrever aqui algo: a posse não
   * concede, e `resolvePassives` não tem mais como ver uma joia.
   */
  sovereignJewels: z
    .array(
      z.object({
        definitionId: z.string().min(1),
        instanceId: z.uuid(),
      }),
    )
    .default([]),
  total: z.object({
    damageBps: z.string(),
    maxHealthBps: z.string(),
    baseHealth: z.string(),
    baseAttackSpeedMicro: z.string(),
    goldPerKill: z.string(),
    goldBps: z.string(),
    regenMilliPerSecond: z.string(),
    babyAlienChanceMicroPp: z.string(),
  }),
});

export type ArtifactPassiveSummary = z.infer<typeof artifactPassiveSummarySchema>;

/** Version of the artifact catalog's MEANING, not of the code. */
export const ARTIFACT_CONTENT_VERSION = 1;

export function toPassiveSummary(passives: ResolvedPassives): ArtifactPassiveSummary {
  return {
    contentVersion: ARTIFACT_CONTENT_VERSION,
    entries: passives.artifacts.map((artifact) => ({
      definitionId: artifact.definitionId,
      instanceId: artifact.instanceId,
      level: artifact.level,
    })),
    /*
     * Vazia, e escrita à mão em vez de omitida (gate V28F.A).
     *
     * `.default([])` produziria o mesmo valor, e escrevê-lo aqui diz uma coisa
     * que a omissão não diria: uma sessão NOVA não tem joias soberanas
     * congeladas, e não é um campo esquecido. A posse não concede, e
     * `resolvePassives` já não tem sequer como ver uma joia.
     */
    sovereignJewels: [],
    total: {
      damageBps: passives.total.damageBps.toString(10),
      maxHealthBps: passives.total.maxHealthBps.toString(10),
      baseHealth: passives.total.baseHealth.toString(10),
      baseAttackSpeedMicro: passives.total.baseAttackSpeedMicro.toString(10),
      goldPerKill: passives.total.goldPerKill.toString(10),
      goldBps: passives.total.goldBps.toString(10),
      regenMilliPerSecond: passives.total.regenMilliPerSecond.toString(10),
      babyAlienChanceMicroPp: passives.total.babyAlienChanceMicroPp.toString(10),
    },
  };
}

export const EMPTY_PASSIVE_SUMMARY: ArtifactPassiveSummary = Object.freeze(
  toPassiveSummary(EMPTY_PASSIVES),
);

/** Reads a persisted summary back into exact bigints. */
export function totalFromSummary(summary: ArtifactPassiveSummary): ScaledArtifactEffects {
  return {
    damageBps: BigInt(summary.total.damageBps),
    maxHealthBps: BigInt(summary.total.maxHealthBps),
    baseHealth: BigInt(summary.total.baseHealth),
    baseAttackSpeedMicro: BigInt(summary.total.baseAttackSpeedMicro),
    goldPerKill: BigInt(summary.total.goldPerKill),
    goldBps: BigInt(summary.total.goldBps),
    regenMilliPerSecond: BigInt(summary.total.regenMilliPerSecond),
    babyAlienChanceMicroPp: BigInt(summary.total.babyAlienChanceMicroPp),
  };
}

/** Parses a persisted summary, throwing rather than guessing. */
export function parsePassiveSummary(value: unknown): ArtifactPassiveSummary {
  return artifactPassiveSummarySchema.parse(value);
}

/**
 * A stable, readable fingerprint of the passives an epoch was earned under.
 *
 * `a:{definition}@{level}:{instanceId}` per artifact, joined in the sorted
 * order `resolvePassives` guarantees. The INSTANCE id is in there for the same
 * reason it is in the loadout fingerprint: combining two copies produces a
 * different economic object, and an offline average must not carry over.
 */
export function passiveFingerprint(summary: ArtifactPassiveSummary): string {
  if (summary.entries.length === 0) {
    return 'a:none';
  }
  return summary.entries
    .map((entry) => `a:${entry.definitionId}@${entry.level}:${entry.instanceId}`)
    .join(',');
}
